<template>
    <Modal class="order-error-modal modl--danger"
           data-component="OrderErrorModal"
           icon="fa-solid fa-triangle-exclamation"
           icon-color="red"
           title="ORDER_ERROR.MODAL_TITLE">
        <p class="text-center">
            {{ l(errors.length > 1 ? 'ORDER_ERROR.INTRO--MULTIPLE' : 'ORDER_ERROR.INTRO') }}
        </p>

        <ErrorMessage v-for="(error, i) in errors"
                      :key="i"
                      :error="error"
                      :show-icon="false">
            {{ error.errorID ? 'Error ID: ' + error.errorID : '' }}
        </ErrorMessage>

        <PageLoadingSpinner v-if="checkingSavedOrder" />
        <template v-else-if="savedOrder">
            <!-- We found a matching order on the server, so at least some of the order made it. -->
            <!-- It's possible that there are missing payments or items. -->

            <div class="alert alert-info">
                {{ l('ORDER_ERROR.DESC_SAVED_1', { bookingRef: savedOrder.bookingRef }) }}
            </div>

            <div v-if="hasPaymentErrors"
                 class="alert alert-warning">
                {{ l('ORDER_ERROR.PAYMENT_ERRORS') }}
            </div>
            <p v-else
               class="text-center">
                {{ l('ORDER_ERROR.DESC_SAVED_2') }}
            </p>
        </template>
        <template v-else-if="isNetworkError">
            <!-- This was a network error. The device could be offline. -->
            <!-- It's still possible the order was saved, because the request could have been sent before the network
            error occurred (e.g. a timeout). So this branch occurs after the savedOrder branch. -->

            <p class="text-center">
                {{ l('ORDER_ERROR.DESC_NETWORK_1') }}
            </p>
            <p class="text-center">
                {{ l('ORDER_ERROR.DESC_NETWORK_2') }}
            </p>
        </template>
        <template v-else>
            <!-- We didn't find a matching order on the server. They could attempt to send the order again. -->

            <p class="text-center">
                {{ l('ORDER_ERROR.DESC_UNSAVED_1') }}
            </p>
            <p class="text-center">
                {{ l('ORDER_ERROR.DESC_UNSAVED_2') }}
            </p>
        </template>

        <p class="text-center mt-2">
            <small>{{ l('ORDER_ERROR.CONTACT') }}</small>
            <template v-if="currentDevice || cart?.thirdPartyRef">
                <br />
                <small v-if="currentDevice">
                    {{ l('ORDER_ERROR.DEVICE' ) }}: {{ currentDevice.ID }}
                </small>
                &nbsp;
                <small v-if="cart?.thirdPartyRef">
                    {{ l('ORDER_ERROR.ORDER_REFERENCE') }}: {{ cart.thirdPartyRef }}
                </small>
            </template>
        </p>

        <template v-slot:footer>
            <div class="modl-footer">
                <!-- Abandon order button -->
                <!-- This first button is a dangerous one. But it's not red because that makes it too enticing. -->
                <SquareBtn v-if="abandonEnabled"
                           color="default"
                           class="cancel-btn"
                           data-analytics-id="Order Error Modal / Abandon Order"
                           size="lg"
                           @click="abandon">
                    <i class="fa-solid fa-diamond-exclamation"></i>
                    {{ l('ORDER_ERROR.ABANDON_BTN') }}
                </SquareBtn>

                <!-- Print receipt button -->
                <SquareBtn color="default"
                           data-analytics-id="Order Error Modal / Reprint Receipt"
                           size="lg"
                           :loading="printing"
                           @click="print">
                    <i class="fa-solid fa-print"></i>
                    {{ l(cart.receiptPrintedAt ? 'ORDER_ERROR.REPRINT_BTN' : 'ORDER_ERROR.PRINT_BTN') }}
                </SquareBtn>

                <!-- Retry later button -->
                <SquareBtn v-if="isNetworkError"
                           color="default"
                           data-analytics-id="Order Error Modal / Retry When Online"
                           size="lg"
                           :loading="printing"
                           @click="storeOrderForLaterProcessing">
                    <i class="fa-solid fa-wifi-exclamation"></i>
                    {{ l('ORDER_ERROR.RETRY_ONLINE_BTN') }}
                </SquareBtn>

                <!-- Retry now button -->
                <SquareBtn v-if="retryEnabled"
                           color="primary"
                           data-analytics-id="Order Error Modal / Retry"
                           size="lg"
                           @click="retry">
                    <i class="fa-solid fa-arrow-rotate-right"></i>
                    {{ isNetworkError ? l('ORDER_ERROR.RETRY_NOW_BTN') : l('ORDER_ERROR.RETRY_BTN') }}
                </SquareBtn>

                <!-- View order button -->
                <SquareBtn v-if="viewOrderEnabled"
                           color="info"
                           data-analytics-id="Order Error Modal / View Order"
                           size="lg"
                           @click="viewOrder">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    {{ l('ORDER_ERROR.VIEW_ORDER_BTN') }}
                </SquareBtn>

                <!-- Continue button - shown if there are also payment errors -->
                <SquareBtn v-if="continueToPaymentErrorsEnabled"
                           color="default"
                           data-analytics-id="Order Error Modal / Continue To Payment Errors"
                           size="lg"
                           @click="continueToPaymentError">
                    <i class="fa-solid fa-arrow-right"></i>
                    {{ l('ORDER_ERROR.CONTINUE') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
/**
 * @typedef {object} OrderErrorModalResult
 * @property {OrderErrorModalAction} action - The action the user took to resolve the error.
 */

import CheckoutErrorActions from '@/lib/Checkout/CheckoutErrorActions';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SquareBtn from '@/vue/components/SquareBtn';
import TradingSessionEvent from '@/lib/TradingSessions/TradingSessionEvent';
import TradingSessionEventTypeEnun from '@/lib/TradingSessions/TradingSessionEventType';
import { container } from '@/container';
import { mapState } from 'vuex';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: {
        PageLoadingSpinner,
        ErrorMessage,
        SquareBtn,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Cart}
         */
        cart: {
            type: Object,
            required: true,
        },

        /**
         * We still need to pass in the CartService too because that's needed to make an order from a cart.
         * This could be got from the container, but that hides this issue that needs to be addressed.
         * TODO: https://digitickets.atlassian.net/browse/PROP-540
         *
         * @type {CartService}
         */
        cartService: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            /**
             * We'll try to look up the order on the server using the thirdPartyRef.
             * If that succeeds, this will be the order we found.
             *
             * @type {?Order}
             */
            savedOrder: null,

            /**
             * @type {boolean}
             */
            checkingSavedOrder: false,

            /**
             * @type {boolean}
             */
            printing: false,
        };
    },

    computed: {
        ...mapState(['currentDevice']),

        errors() {
            return this.cart.getUnresolvedOrderSaveErrors();
        },

        hasPaymentErrors() {
            return this.cart.hasUnresolvedPaymentSaveErrors();
        },

        latestError() {
            return this.errors[this.errors.length - 1];
        },

        isNetworkError() {
            return this.latestError?.name === 'NetworkApiError';
        },

        abandonEnabled() {
            return !this.savedOrder && !this.hasPaymentErrors;
        },

        retryEnabled() {
            return !this.savedOrder;
        },

        viewOrderEnabled() {
            return !!this.savedOrder && !this.hasPaymentErrors;
        },

        continueToPaymentErrorsEnabled() {
            return this.hasPaymentErrors;
        },
    },

    async created() {
        // We'll have a thirdPartyRef from the cart. We can use that to ask the API if the order actually made it.
        if (this.cart.thirdPartyRef) {
            this.checkingSavedOrder = true;
            try {
                // TODO: We can avoid calling this twice as it is also called in CheckoutService.
                // Maybe that could store it on the cart?
                this.savedOrder = await container.ordersApi.findByThirdPartyRef(this.cart.thirdPartyRef);
                // We always deal with the original order not a specific adjustment, so tweak some refs and IDs.
                this.savedOrder.ID = this.savedOrder.originalOrderID || this.savedOrder.ID;
                this.savedOrder.bookingRef = this.savedOrder.originalRef;
            } catch {
                // No saved order found with this ref.
            } finally {
                this.checkingSavedOrder = false;
            }
        }

        // Always print a receipt on error (if we haven't already printed one).
        if (!this.cart.receiptPrintedAt) {
            await this.print();
        }
    },

    methods: {
        async abandon() {
            let privilegeResult;
            try {
                // Require supervisor confirmation to abandon the order.
                privilegeResult = await container.privilegesManager.requirePrivilege(PrivilegeNames.ABANDON_PAYMENTS);
            } catch {
                // No permission - quit.
                return;
            }

            // Prompt for confirmation.
            const confirmed = await container.dialogService.confirm('ORDER_ERROR.CONFIRM_ABANDON');
            if (!confirmed) {
                return;
            }

            // Log a trading session entry.
            // TODO: We could prompt for notes from the user such as what happened and store them as the narrative?
            const tradingSessionEvent = (new TradingSessionEvent(TradingSessionEventTypeEnun.ABANDON_ORDER))
                .setOperatorID(container.userService.getUserID())
                .setManagerID(privilegeResult.grantedBy)
                .setAmount(this.cart.getTotal())
                .setData({
                    thirdPartyRef: this.cart.thirdPartyRef,
                });
            await container.tradingSessionManager.saveEvent(tradingSessionEvent);

            this.resolve({
                action: CheckoutErrorActions.ABANDON,
            });
        },

        async print() {
            this.printing = true;

            // It would seem that if the order has actually been saved, we can print a real receipt including the
            // items returned from the API.
            // However, depending on when the error occurred, the order may have been saved but without its items.
            // In some testing I found errors in certain places in the API store lines in orders_items but these
            // don't become visible in view_orders_items_active.

            // We want to produce a receipt here and be certain it contains the items that were sold.
            // So the best we can do is create the same receipt an offline order creates (convert the cart to an order).
            // And if we have a bookingRef we can add that to the generated order.

            // Create an order from the cart.
            const order = container.cartToOrderMapper.buildOrderFromApiConfirmation(
                // TODO: https://digitickets.atlassian.net/browse/PROP-540
                this.cartService,
                null
            );

            // Enhance the generated order with data from the real order if we have one.
            if (this.savedOrder?.bookingRef) {
                order.bookingRef = this.savedOrder.bookingRef;
            }
            if (this.savedOrder?.originalRef) {
                order.originalRef = this.savedOrder.originalRef;
            }

            // Note we can't print an adjustment receipt from here, only regular receipts.
            try {
                await container.orderReceiptPrinter.printOrderReceipt(
                    order,
                    null,
                    null,
                    false,
                    {
                        errorIDs: this.cart.getErrorIDs(),
                    }
                );
                await container.cartService.modifyCurrentCart((cart) => {
                    cart.receiptPrintedAt = new Date();
                });
            } finally {
                this.printing = false;
            }
        },

        async storeOrderForLaterProcessing() {
            this.resolve({
                action: CheckoutErrorActions.STORE_FOR_LATER,
            });
        },

        async retry() {
            this.resolve({
                action: CheckoutErrorActions.RETRY,
            });
        },

        async viewOrder() {
            container.navigationService.viewOrderDetails(this.savedOrder.getOriginalRef());

            this.resolve({
                action: CheckoutErrorActions.VIEW_ORDER,
            });
        },

        async continueToPaymentError() {
            this.resolve({
                action: CheckoutErrorActions.CONTINUE_TO_PAYMENT_ERRORS,
            });
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

.order-error-modal {
    &__error {
        background: @dt-red;
        color: #fff;
        border-radius: 5px;
        padding: 15px;
        margin-bottom: 15px;
    }
}
</style>
